import { FileData } from 'src/components/textEditor/TextEditor';
import { MediaData } from 'src/types/helios/Media';
import {
  MediaResponse,
  MediaResponses,
  Question,
  QuestionDetails,
  MediaIdsMap,
  SubQuestion,
} from 'src/types/helios/Question';
import { FileMetadata } from 'src/components/FileDisplay';
import { IMAGE_EXTENSIONS_NO_DOT } from 'src/contstant/fileConstants';

/**
 * Extracts base64 image data from HTML content
 * @param content HTML content containing base64 images
 * @returns Array of FileData objects with image data and extension
 */
export const extractSrcValues = (content: string): string[] => {
  if (!content) return [];

  const regex = /src="([^"]+)"/g;
  const matches: string[] = [];
  let match;

  while ((match = regex.exec(content)) !== null) {
    const src = match[1];

    // For non-base64 images, we'll just store the src as is
    matches.push(src);
  }

  return matches;
};

/**
 * Extracts only media UUID src values from HTML content.
 * Filters out base64 data URIs and presigned/external HTTP URLs,
 * returning only the raw media IDs stored as src attributes.
 * @param content HTML content to extract media IDs from
 * @returns Array of media ID strings
 */
export const extractMediaIdSrcValues = (content: string): string[] =>
  extractSrcValues(content).filter(
    (src) => !src.startsWith('data:') && !src.startsWith('http'),
  );

/**
 * Replaces base64 image data with mediaId in content
 * @param content HTML content containing base64 images
 * @param imageMap Map of base64 image data to mediaId
 * @returns Updated content with mediaIds instead of base64 data
 */
export const replaceImagesWithMediaIds = (
  content: string,
  imageMap: Record<string, string>,
): string => {
  try {
    if (!content) return '';

    let updatedContent = content;
    Object.entries(imageMap).forEach(([base64Image, mediaId]) => {
      updatedContent = updatedContent.replaceAll(base64Image, mediaId);
    });
    return updatedContent;
  } catch (e) {
    console.log('error in replaceImagesWithMediaIds', e);
    return content;
  }
};

/**
 * Replaces presigned URLs with media IDs in question content
 * @param question The question object containing content and media mappings
 * @param fieldName The field name in defaultLanguageContent.questionDetails to process
 * @returns Updated content with mediaIds instead of presigned URLs
 */
export const replacePresignedUrlsWithMediaIdsForQuestion = (
  content: string,
  mediaResponses: MediaResponses,
): string => {
  try {
    if (!content || !mediaResponses) return content;
    let updatedContent = content;

    for (const category in mediaResponses) {
      const responses = mediaResponses[category as keyof MediaResponses];
      if (responses) {
        for (const response of responses) {
          // Extract the base URL pattern that identifies this media (without query params)
          const urlBase = response.presignedUrl.split('?')[0];
          const urlPattern = new RegExp(`${urlBase}[^"']*`, 'g');
          updatedContent = updatedContent.replace(urlPattern, response.media.mediaId);
        }
      }
    }

    return updatedContent;
  } catch (e) {
    console.log('error in replaceImagesWithMediaIds', e);
    return content;
  }
};

/**
 * Replaces media IDs with presigned URLs in question content
 * @param content HTML content containing media IDs
 * @param mediaResponses Media responses containing ID to URL mappings
 * @returns Updated content with presigned URLs instead of media IDs
 */
export const replaceMediaIdsWithPresignedUrlsForQuestion = (
  content: string,
  mediaResponses: MediaResponses,
): string => {
  try {
    let updatedContent = content;

    for (const category in mediaResponses) {
      const responses = mediaResponses[category as keyof MediaResponses];
      if (responses) {
        for (const response of responses) {
          // Extract the base URL pattern that identifies this media (without query params)
          const mediaIdPattern = new RegExp(
            `src="([^"]*${response.media.mediaId}[^"]*)"`,
            'g',
          );
          updatedContent = updatedContent.replace(
            mediaIdPattern,
            `src="${response.presignedUrl}"`,
          );
        }
      }
    }

    return updatedContent;
  } catch (e) {
    console.log('error in replaceImagesWithMediaIds', e);
    return content;
  }
};

/**
 * Replaces presigned URLs with media IDs in sub-question content
 * @param content HTML content containing presigned URLs
 * @param mediaResponses Optional array of media responses for URL to ID mapping
 * @returns Updated content with media IDs instead of presigned URLs
 */
export const replacePresignedUrlsWithMediaIdsForSubQuestion = (
  content: string,
  mediaResponses?: MediaResponse[],
): string => {
  try {
    let updatedContent = content;
    if (!mediaResponses) return updatedContent;

    for (const response of mediaResponses) {
      // Extract the base URL pattern that identifies this media (without query params)
      const urlBase = response.presignedUrl.split('?')[0];
      const urlPattern = new RegExp(`${urlBase}[^"']*`, 'g');
      updatedContent = updatedContent.replace(urlPattern, response.media.mediaId);
    }

    return updatedContent;
  } catch (e) {
    console.log('error in replaceImagesWithMediaIds', e);
    return content;
  }
};

export const replaceMediaIdsWithPresignedUrlsForQuestionSubQuestion = (
  content: string,
  mediaResponses?: MediaResponse[],
): string => {
  try {
    let updatedContent = content;
    if (!mediaResponses) return updatedContent;
    for (const response of mediaResponses) {
      // Extract the base URL pattern that identifies this media (without query params)
      const mediaIdPattern = new RegExp(
        `src="([^"]*${response.media.mediaId}[^"]*)"`,
        'g',
      );
      updatedContent = updatedContent.replace(
        mediaIdPattern,
        `src="${response.presignedUrl}"`,
      );
    }

    return updatedContent;
  } catch (e) {
    console.log('error in replaceImagesWithMediaIds', e);
    return content;
  }
};

/**
 * Replaces presigned URLs with media IDs in any content using MediaData array.
 * Symmetric counterpart to replaceMediaIdsWithPresignedUrlsAnyContent.
 * @param content HTML content containing presigned URLs
 * @param mediaResponses MediaData array from Redux store for URL to ID mapping
 * @returns Updated content with media IDs instead of presigned URLs
 */
export const replacePresignedUrlsWithMediaIdsAnyContent = (
  content: string,
  mediaResponses?: MediaData[],
): string => {
  try {
    let updatedContent = content;
    if (!mediaResponses) return updatedContent;

    for (const response of mediaResponses) {
      const urlBase = response.presignedUrl.split('?')[0];
      const urlPattern = new RegExp(`${urlBase}[^"']*`, 'g');
      updatedContent = updatedContent.replace(urlPattern, response.media.mediaId);
    }

    return updatedContent;
  } catch (e) {
    console.log('error in replacePresignedUrlsWithMediaIdsAnyContent', e);
    return content;
  }
};

export const replaceMediaIdsWithPresignedUrlsAnyContent = (
  content: string,
  mediaResponses?: MediaData[],
): string => {
  try {
    let updatedContent = content;
    if (!mediaResponses) return updatedContent;

    for (const response of mediaResponses) {
      const mediaIdPattern = new RegExp(
        `src="([^"]*${response.media.mediaId}[^"]*)"`,
        'g',
      );
      updatedContent = updatedContent.replace(
        mediaIdPattern,
        `src="${response.presignedUrl}"`,
      );
    }

    return updatedContent;
  } catch (e) {
    console.log('error in replaceImagesWithMediaIds', e);
    return content;
  }
};

/**
 * Extracts all media IDs from a question object
 * Backend always includes both embedded images and standalone documents in mediaIdsMap
 * Also collects media IDs from sub-questions in localizedContent
 * @param question The question object to extract media IDs from
 * @returns Array of unique media IDs found in the question
 */
export const getAllMediaIdsfromQuestionForAudit = (question: Question): string[] => {
  try {
    let mediaIds: string[] = [];

    // Extract all media IDs from mediaIdsMap
    // Backend includes both embedded images AND standalone documents
    if (question.mediaIdsMap) {
      Object.values(question.mediaIdsMap).forEach((ids) => {
        if (ids && Array.isArray(ids)) {
          mediaIds.push(...ids);
        }
      });
    }

    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

    // Extracts all media IDs from a single subQuestion (deprecated mediaIds,
    // newer mediaIdsMap, and UUID src fallback from instruction HTML)
    const extractSubQuestionMediaIds = (subQuestion: any): string[] => {
      const ids: string[] = [];
      if (subQuestion.mediaIds && Array.isArray(subQuestion.mediaIds)) {
        ids.push(...subQuestion.mediaIds);
      }
      if (subQuestion.mediaIdsMap) {
        Object.values(subQuestion.mediaIdsMap).forEach((mapIds) => {
          if (mapIds && Array.isArray(mapIds)) {
            ids.push(...(mapIds as string[]));
          }
        });
      }
      // Fallback: extract UUIDs directly from instruction HTML
      if (subQuestion.instruction) {
        extractSrcValues(subQuestion.instruction).forEach((src) => {
          if (uuidPattern.test(src)) ids.push(src);
        });
      }
      return ids;
    };

    // Extract from subQuestions in localizedContent
    const localizedSubQuestions = question.localizedContent?.content?.subQuestions;
    if (localizedSubQuestions && Array.isArray(localizedSubQuestions)) {
      localizedSubQuestions.forEach((sq) =>
        mediaIds.push(...extractSubQuestionMediaIds(sq)),
      );
    }

    // Extract from top-level subQuestions (outside localizedContent)
    if (question.subQuestions && Array.isArray(question.subQuestions)) {
      question.subQuestions.forEach((sq) =>
        mediaIds.push(...extractSubQuestionMediaIds(sq)),
      );
    }

    // Deduplicate and return unique media IDs
    return Array.from(new Set(mediaIds));
  } catch (e) {
    console.log('error in getAllMediaIdsfromQuestionForAudit', e);
    return [];
  }
};

/**
 * Extracts base64 encoded files from HTML content
 * @param content HTML content that may contain base64 data URIs
 * @returns Array of FileData objects containing base64 images and their MIME types
 */
export function extractBase64Files(content: string): FileData[] {
  try {
    // Regular expression to match base64 patterns with format
    const base64Regex = /data:([a-zA-Z0-9]+\/[a-zA-Z0-9-.+]+);base64,([a-zA-Z0-9+/=]+)/g;
    const files: FileData[] = [];

    // Find all matches in the content
    let match;
    while ((match = base64Regex.exec(content)) !== null) {
      const mimeType = match[1]; // e.g., "image/jpeg"

      files.push({
        image: match[0], // full base64 string with data URI
        extension: mimeType,
      });
    }

    return files;
  } catch (e) {
    console.log('error in getAllMediaIdsfromQuestionForAudit', e);
    return [];
  }
}

/**
 * Converts browser File objects to FileData format with base64 encoding
 * @param files Array of File objects from file input/upload
 * @returns Promise resolving to array of FileData objects
 */
export async function convertFilesToFileData(files: File[]): Promise<FileData[]> {
  if (!files || files.length === 0) {
    return [];
  }

  const conversionPromises = files.map((file) => {
    return new Promise<FileData>((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        // Extract filename without extension
        const lastDotIndex = file.name.lastIndexOf('.');
        const fileName =
          lastDotIndex !== -1 ? file.name.substring(0, lastDotIndex) : file.name;

        resolve({
          image: e.target?.result as string,
          extension: file.type,
          fileName: fileName,
        });
      };

      reader.onerror = () => {
        reject(new Error(`Failed to read file: ${file.name}`));
      };

      reader.readAsDataURL(file);
    });
  });

  try {
    return await Promise.all(conversionPromises);
  } catch (error) {
    console.error('Error converting files to FileData:', error);
    return [];
  }
}

/**
 * Merges existing document metadata mediaIds with newly uploaded document mediaIds
 * @param existingMetadata Existing document metadata mapped by field name
 * @param newDocumentFiles New document files to upload mapped by field name
 * @param uploadedMediaIds Array of mediaIds from newly uploaded documents
 * @param existingMediaIdsMap Current mediaIds map containing embedded image IDs
 * @returns Updated MediaIdsMap with merged document mediaIds
 */
export function mergeMediaIdsFromDocuments(
  existingMetadata: Record<string, FileMetadata[]>,
  newDocumentFiles: Record<string, File[]>,
  uploadedMediaIds: string[],
  existingMediaIdsMap: MediaIdsMap,
): MediaIdsMap {
  const updatedMediaIdsMap: MediaIdsMap = { ...existingMediaIdsMap };

  // First, preserve existing document mediaIds from metadata
  Object.keys(existingMetadata).forEach((fieldName) => {
    const metadata = existingMetadata[fieldName as keyof MediaIdsMap];
    if (metadata && metadata.length > 0) {
      const existingDocMediaIds = metadata.map((item) => item.mediaId);
      // Merge with existing mediaIds from embedded images
      const embeddedMediaIds = updatedMediaIdsMap[fieldName as keyof MediaIdsMap] || [];
      updatedMediaIdsMap[fieldName as keyof MediaIdsMap] = [
        ...embeddedMediaIds,
        ...existingDocMediaIds,
      ];
    }
  });

  // Then, add newly uploaded document mediaIds
  const fieldNames = Object.keys(newDocumentFiles) as (keyof MediaIdsMap)[];
  let documentIndex = 0;

  fieldNames.forEach((fieldName) => {
    const fieldDocCount = newDocumentFiles[fieldName]?.length || 0;
    if (fieldDocCount > 0) {
      // Get document mediaIds for this field
      const fieldDocMediaIds = uploadedMediaIds.slice(
        documentIndex,
        documentIndex + fieldDocCount,
      );

      // Add to existing mediaIdsMap (which now includes preserved metadata)
      const currentMediaIds = updatedMediaIdsMap[fieldName] || [];
      updatedMediaIdsMap[fieldName] = [...currentMediaIds, ...fieldDocMediaIds];

      documentIndex += fieldDocCount;
    }
  });

  return updatedMediaIdsMap;
}

/**
 * Groups documents by field name from media list and question mediaIdsMap
 * Filters out image files and returns only document files
 * @param currentQuestion The question object containing mediaIdsMap
 * @param mediaList Array of media data with presigned URLs
 * @param isLoading Whether media is still loading
 * @returns Record mapping field names to arrays of document metadata
 */
export function getDocumentsByField(
  currentQuestion: Question | undefined,
  mediaList: MediaData[],
  isLoading: boolean,
): Record<string, Array<{ fileName: string; mediaId: string; presignedUrl: string }>> {
  const map: Record<
    string,
    Array<{ fileName: string; mediaId: string; presignedUrl: string }>
  > = {};

  // Don't build map if media isn't loaded yet
  if (isLoading || mediaList.length === 0 || !currentQuestion?.mediaIdsMap) {
    return map;
  }

  // Iterate through each field in mediaIdsMap
  Object.entries(currentQuestion.mediaIdsMap).forEach(([fieldName, fieldMediaIds]) => {
    // Skip if fieldMediaIds is undefined or not an array
    if (!fieldMediaIds || !Array.isArray(fieldMediaIds)) {
      return;
    }

    // Filter mediaList for documents in this field
    map[fieldName] = mediaList
      .filter((mediaData) => {
        const isInField = fieldMediaIds.includes(mediaData.media.mediaId);
        const isDocument = !IMAGE_EXTENSIONS_NO_DOT.includes(
          mediaData.media.extension?.toLowerCase() || ('' as any),
        );
        return isInField && isDocument;
      })
      .map((mediaData) => ({
        fileName: mediaData.media.fileName || 'document',
        mediaId: mediaData.media.mediaId,
        presignedUrl: mediaData.presignedUrl,
      }));
  });

  return map;
}

/**
 * Migrates legacy mediaIds array to mediaIdsMap structure for backward compatibility
 * Old questions may have a flat mediaIds array on subQuestions
 * This function converts them to the new mediaIdsMap structure for consistency
 *
 * @param subQuestion - SubQuestion that may have legacy mediaIds
 * @returns SubQuestion with mediaIdsMap populated from legacy mediaIds if needed
 */
export const migrateSubQuestionMediaIds = (subQuestion: SubQuestion): SubQuestion => {
  // If already has mediaIdsMap or no legacy mediaIds, return as-is
  if (
    subQuestion.mediaIdsMap ||
    !subQuestion.mediaIds ||
    subQuestion.mediaIds.length === 0
  ) {
    return subQuestion;
  }

  // Migrate legacy mediaIds to mediaIdsMap
  // Since we don't know which field each mediaId belongs to in the legacy format,
  // we'll distribute them based on what fields exist in the subQuestion
  const mediaIdsMap: { instruction?: string[]; taskDescription?: string[] } = {};

  // Extract media IDs that are actually in the instruction content
  if (subQuestion.instruction) {
    const instructionMediaIds = extractSrcValues(subQuestion.instruction);
    if (instructionMediaIds.length > 0) {
      mediaIdsMap.instruction = instructionMediaIds;
    }
  }

  // Extract media IDs that are actually in the taskDescription content
  if (subQuestion.taskDetails?.taskDescription) {
    const taskDescMediaIds = extractSrcValues(subQuestion.taskDetails.taskDescription);
    if (taskDescMediaIds.length > 0) {
      mediaIdsMap.taskDescription = taskDescMediaIds;
    }
  }

  return {
    ...subQuestion,
    mediaIdsMap,
    // Keep mediaIds for backward compatibility when reading, but we won't save it
  };
};

/**
 * Replaces media IDs with presigned URLs in subQuestions for display
 * The backend stores media IDs in HTML, but we need presigned URLs to render images
 *
 * @param subQuestions - Array of subQuestions to process
 * @returns Updated subQuestions with presigned URLs in place of media IDs
 */
export const replaceMediaIdsWithPresignedUrlsInSubQuestions = (
  subQuestions: any[],
): any[] => {
  if (!subQuestions?.length) return subQuestions;

  return subQuestions.map((subQuestion) => {
    // Use mediaResponsesMap if available (new structure), fallback to mediaResponses (legacy)
    const instructionMediaResponses =
      subQuestion.mediaResponsesMap?.instruction || subQuestion.mediaResponses;
    const taskDescriptionMediaResponses =
      subQuestion.mediaResponsesMap?.taskDescription || subQuestion.mediaResponses;

    return {
      ...subQuestion,
      // Replace media IDs with presigned URLs in instruction
      instruction: replaceMediaIdsWithPresignedUrlsForQuestionSubQuestion(
        subQuestion.instruction,
        instructionMediaResponses,
      ),
      // Handle nested taskDetails if it exists
      taskDetails: subQuestion.taskDetails && {
        ...subQuestion.taskDetails,
        // Replace media IDs with presigned URLs in taskDescription
        taskDescription: replaceMediaIdsWithPresignedUrlsForQuestionSubQuestion(
          subQuestion.taskDetails.taskDescription,
          taskDescriptionMediaResponses,
        ),
      },
    };
  });
};

/**
 * Converts MediaResponse array to MediaData array format for SafeHtml component
 * @param mediaResponses Optional array of MediaResponse objects
 * @returns Array of MediaData objects with proper EntityType
 */
export const convertMediaResponseToMediaData = (
  mediaResponses?: MediaResponse[],
): MediaData[] => {
  if (!mediaResponses) return [];
  return mediaResponses.map((mr) => ({
    media: {
      mediaId: mr.media.mediaId,
      entityType: mr.media.entityType as any,
      s3Key: mr.media.s3Key || '',
      fileName: mr.media.fileName || '',
      extension: mr.media.extension || '',
      fileSize: mr.media.fileSize || 0,
      program: mr.media.program,
      businessUnit: mr.media.businessUnit,
      createdAt: mr.media.createdAt ? new Date(mr.media.createdAt) : undefined,
      createdBy: mr.media.createdBy || '',
      lastUpdatedAt: mr.media.lastUpdatedAt
        ? new Date(mr.media.lastUpdatedAt)
        : undefined,
      lastUpdatedBy: mr.media.lastUpdatedBy,
    },
    presignedUrl: mr.presignedUrl,
  }));
};
